import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { downloadPdf } from "@/lib/pdf";
import { CreditCard, Plus, X, Save, Trash2, Banknote, Search, Download, Send, CheckCircle2, FileCheck, RefreshCw, XCircle, Clock } from "lucide-react";
import { toast } from "sonner";
import { confirmAction } from "@/lib/confirm";

const fmtEUR = (n) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(Number(n) || 0);

const PAY_STATUS = {
  non_paye: { label: "Non payée", cls: "badge-warning" },
  partiel: { label: "Partielle", cls: "badge-accent" },
  paye: { label: "Soldée", cls: "badge-success" },
};

// Statuts DGFiP (Y-Model) renvoyés par Iopole — affichage côté entrepreneur
const PDP_STATUS = {
  Deposited: { label: "Déposée", cls: "badge-muted", icon: Clock },
  Sent: { label: "Transmise", cls: "badge-accent", icon: Send },
  Received: { label: "Reçue", cls: "badge-accent", icon: CheckCircle2 },
  Processed: { label: "Traitée", cls: "badge-accent", icon: CheckCircle2 },
  Accepted: { label: "Acceptée", cls: "badge-success", icon: CheckCircle2 },
  Rejected: { label: "Rejetée", cls: "badge-warning", icon: XCircle },
  Expired: { label: "Expirée", cls: "badge-warning", icon: XCircle },
};

export default function Invoices() {
  const [items, setItems] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [clients, setClients] = useState([]);
  const [modal, setModal] = useState(null);
  const [payModal, setPayModal] = useState(null);
  const [q, setQ] = useState("");

  const load = async () => {
    const [i, qq, c] = await Promise.all([api.get("/invoices"), api.get("/quotes"), api.get("/clients")]);
    setItems(i.data); setQuotes(qq.data); setClients(c.data);
  };
  useEffect(() => { load(); }, []);

  const clientMap = Object.fromEntries(clients.map((c) => [c.id, c]));
  const filtered = items.filter((x) => !q || `${x.number} ${clientMap[x.client_id]?.last_name || ""}`.toLowerCase().includes(q.toLowerCase()));

  const remove = async (id) => {
    if (!(await confirmAction("Supprimer cette facture ?"))) return;
    await api.delete(`/invoices/${id}`); await load();
  };

  const downloadPdfInvoice = (i) => downloadPdf(`/invoices/${i.id}/pdf`, `${i.number || "facture"}.pdf`);
  const downloadFacturX = (i) => downloadPdf(`/invoices/${i.id}/facturx`, `${i.number || "facture"}-facturx.pdf`);

  const [sending, setSending] = useState(null);
  const sendViaPdp = async (i) => {
    if (!(await confirmAction(`Transmettre officiellement la facture ${i.number} à votre client via la PDP Iopole ?\n\nCette transmission est électronique et conforme à la réforme 2027 (Factur-X + XML CII).\n\n⚠️ Une fois envoyée, la facture ne peut plus être modifiée (règles DGFiP).`, { confirmLabel: "Transmettre" }))) return;
    setSending(i.id);
    try {
      const r = await api.post(`/invoices/${i.id}/send-via-pdp`);
      toast.success(`Facture envoyée à Iopole. ID : ${r.data?.result?.id || "—"}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Envoi PDP échoué");
    } finally {
      setSending(null);
    }
  };

  const [refreshingPdp, setRefreshingPdp] = useState(null);
  const refreshPdpStatus = async (i) => {
    setRefreshingPdp(i.id);
    try {
      const r = await api.get(`/invoices/${i.id}/pdp-status?refresh=true`);
      const st = r.data?.current_status;
      if (st) {
        const lbl = PDP_STATUS[st]?.label || st;
        toast.success(`Statut DGFiP : ${lbl}`);
      } else {
        toast.info("Statut non disponible pour l'instant (en cours de traitement par l'État)");
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rafraîchissement échoué");
    } finally {
      setRefreshingPdp(null);
    }
  };

  return (
    <div data-testid="invoices-page" className="max-w-7xl mx-auto">
      <header className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-2 font-body text-xs  text-[#FFC400] font-bold mb-3"><CreditCard size={13} strokeWidth={2.5} /> Module 5 — Factures</div>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight leading-[1.05] text-[var(--chalk)]">Factures</h1>
          <p className="text-[var(--ink-muted)] mt-3 font-body">Convertissez vos devis acceptés en facture. Téléchargez en <b>PDF classique</b>, en <b>Factur-X</b> (conforme réforme 2027) ou envoyez directement via la <b>PDP Iopole</b>.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--grey)]" />
            <input className="input pl-9 w-64" placeholder="N° facture, client..." value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <button onClick={() => setModal("new")} className="btn btn-accent" data-testid="add-invoice"><Plus size={15} /> Nouvelle facture</button>
        </div>
      </header>

      <div className="card overflow-hidden p-0">
        {filtered.length === 0 ? (
          <div className="p-16 text-center text-[var(--grey)]">
            <CreditCard size={36} className="mx-auto mb-3 opacity-40" />
            <div className="font-display text-[var(--chalk)]">Aucune facture</div>
          </div>
        ) : (
          <table className="table">
            <thead><tr>
              <th>N°</th><th>Type</th><th>Client</th><th className="text-right">Total TTC</th><th className="text-right">Payé</th><th className="text-right">Solde</th><th>Statut</th><th className="w-56">Actions</th>
            </tr></thead>
            <tbody>
              {filtered.map((i) => {
                const c = clientMap[i.client_id];
                const ps = PAY_STATUS[i.payment_status] || PAY_STATUS.non_paye;
                // Factur-X et transmission PDP réservés aux clients Pro / Administration publique
                // (les particuliers n'utilisent pas la facturation électronique — envoi PDF classique par email suffit)
                const eligibleFacturX = c && (c.client_type === "pro" || c.client_type === "public");
                return (
                  <tr key={i.id} data-testid={`invoice-${i.id}`}>
                    <td className="font-mono text-xs font-bold">{i.number}</td>
                    <td>{i.is_situation ? <span className="badge badge-success">Situation N°{i.situation_number}</span> : i.is_deposit ? <span className="badge badge-accent">Acompte {i.deposit_pct}%</span> : <span className="badge badge-muted">Facture</span>}</td>
                    <td>{c ? `${c.last_name} ${c.first_name || ""}` : "—"}</td>
                    <td className="text-right font-mono tabular-nums font-bold">{fmtEUR(i.total_ttc)}</td>
                    <td className="text-right font-mono tabular-nums text-[#FFC400]">{fmtEUR(i.paid_amount)}</td>
                    <td className="text-right font-mono tabular-nums text-[#E10600] font-bold">{fmtEUR(i.balance)}</td>
                    <td><span className={`badge ${ps.cls}`}>{ps.label}</span></td>
                    <td>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <button onClick={() => downloadPdfInvoice(i)} className="btn btn-sm btn-outline" title="Télécharger un PDF classique (pour impression ou envoi manuel)" data-testid={`i-pdf-${i.id}`}><Download size={12} /> PDF</button>
                        {eligibleFacturX && (
                          <button onClick={() => downloadFacturX(i)} className="btn btn-sm btn-outline" title="Télécharger le PDF Factur-X (conforme réforme 2027) — pour envoi manuel par email ou upload sur Chorus Pro" data-testid={`i-fx-${i.id}`}><FileCheck size={12} /> Factur-X (fichier)</button>
                        )}
                        {eligibleFacturX && (i.pdp_sent_at ? (
                          (() => {
                            const st = i.pdp_status;
                            const meta = PDP_STATUS[st] || { label: "Envoyée", cls: "badge-accent", icon: Clock };
                            const Icon = meta.icon;
                            const tooltip = st
                              ? `Statut DGFiP : ${meta.label}\nEnvoyée le ${new Date(i.pdp_sent_at).toLocaleString("fr-FR")}${i.pdp_last_polled_at ? `\nDernier check : ${new Date(i.pdp_last_polled_at).toLocaleString("fr-FR")}` : ""}`
                              : `Envoyée le ${new Date(i.pdp_sent_at).toLocaleString("fr-FR")} — statut en attente`;
                            return (
                              <span className="inline-flex items-center gap-1" data-testid={`i-pdp-sent-${i.id}`}>
                                <span className={`badge ${meta.cls} inline-flex items-center gap-1`} title={tooltip}>
                                  <Icon size={11} /> {meta.label}
                                </span>
                                {!i.pdp_is_terminal && (
                                  <button
                                    onClick={() => refreshPdpStatus(i)}
                                    disabled={refreshingPdp === i.id}
                                    className="w-6 h-6 rounded border border-[var(--steel)] text-[var(--ink-muted)] hover:text-[#FFC400] hover:border-[#FFC400] inline-flex items-center justify-center transition-colors disabled:opacity-50"
                                    title="Rafraîchir le statut DGFiP"
                                    data-testid={`i-pdp-refresh-${i.id}`}
                                  >
                                    <RefreshCw size={11} className={refreshingPdp === i.id ? "animate-spin" : ""} />
                                  </button>
                                )}
                              </span>
                            );
                          })()
                        ) : (
                          <button
                            onClick={() => sendViaPdp(i)}
                            disabled={sending === i.id}
                            className="btn btn-sm"
                            style={{
                              background: "linear-gradient(135deg, #FFC400 0%, #E6B000 100%)",
                              color: "#0B0B0C",
                              fontWeight: 700,
                              border: "1px solid #FFC400",
                              boxShadow: "0 0 0 2px rgba(255,196,0,0.2)",
                            }}
                            title="TRANSMISSION OFFICIELLE — Envoie la facture au client via la PDP Iopole (Factur-X électronique conforme réforme 2027)"
                            data-testid={`i-send-pdp-${i.id}`}
                          >
                            <Send size={12} /> {sending === i.id ? "Transmission…" : "Transmettre au client"}
                          </button>
                        ))}
                        {i.payment_status !== "paye" && <button onClick={() => setPayModal(i)} className="btn btn-sm btn-outline" data-testid={`pay-${i.id}`}><Banknote size={12} /> Paiement</button>}
                        <button onClick={() => remove(i.id)} className="w-7 h-7 rounded border border-[var(--steel)] text-[#E10600] hover:bg-[rgba(225,6,0,0.12)] hover:border-[#E10600] inline-flex items-center justify-center transition-colors"><Trash2 size={12} /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {modal && <InvoiceModal quotes={quotes} clients={clients} onClose={() => setModal(null)} onSaved={async () => { await load(); setModal(null); }} />}
      {payModal && <PaymentModal invoice={payModal} onClose={() => setPayModal(null)} onSaved={async () => { await load(); setPayModal(null); }} />}
    </div>
  );
}

function InvoiceModal({ quotes, clients, onClose, onSaved }) {
  const [invoiceType, setInvoiceType] = useState("complete"); // complete | deposit | situation
  const [quoteId, setQuoteId] = useState("");
  const [depositPct, setDepositPct] = useState(30);
  const [situationNumber, setSituationNumber] = useState(1);
  const [cumulativePct, setCumulativePct] = useState(30);
  const [previousPct, setPreviousPct] = useState(0);
  const [situationsData, setSituationsData] = useState(null);
  const [saving, setSaving] = useState(false);

  const acceptedQuotes = quotes.filter((q) => q.status === "accepte" || q.status === "envoye" || q.status === "facture");
  const selected = acceptedQuotes.find((q) => q.id === quoteId);

  // Fetch situations existantes quand on choisit un devis + type situation
  useEffect(() => {
    if (invoiceType === "situation" && quoteId) {
      api.get(`/quotes/${quoteId}/situations`).then((r) => {
        setSituationsData(r.data);
        setSituationNumber(r.data.next_situation_number || 1);
        setPreviousPct(r.data.cumulative_pct || 0);
        setCumulativePct(Math.min(100, (r.data.cumulative_pct || 0) + 30));
      }).catch(() => setSituationsData(null));
    } else {
      setSituationsData(null);
    }
  }, [invoiceType, quoteId]);

  const isDeposit = invoiceType === "deposit";
  const isSituation = invoiceType === "situation";
  const deltaPct = Math.max(0, cumulativePct - previousPct);

  const save = async () => {
    if (!quoteId) { toast.error("Devis requis"); return; }
    if (isSituation && deltaPct <= 0) { toast.error("L'avancement cumulé doit être supérieur au cumul précédent"); return; }
    setSaving(true);
    try {
      const payload = {
        client_id: selected.client_id,
        quote_id: selected.id,
        project_name: selected.project_name,
        lines: selected.lines,
        vat_rate: selected.vat_rate,
        is_deposit: isDeposit,
        deposit_pct: isDeposit ? depositPct : null,
        is_situation: isSituation,
        situation_number: isSituation ? situationNumber : null,
        situation_cumulative_pct: isSituation ? cumulativePct : null,
        previous_cumulative_pct: isSituation ? previousPct : null,
      };
      const { data: created } = await api.post("/invoices", payload);
      const movements = created?.stock_movements || [];
      const baseMsg =
        isDeposit ? `Facture d'acompte ${depositPct}% créée` :
        isSituation ? `Situation N°${situationNumber} créée (+${deltaPct}% = ${cumulativePct}% cumul)` :
        "Facture créée";
      if (movements.length > 0) {
        const lowAfter = movements.filter((m) => m.new_qty <= 0).length;
        toast.success(`${baseMsg} · stock décrémenté pour ${movements.length} article(s)${lowAfter > 0 ? ` — ${lowAfter} en rupture !` : ""}`, { duration: 5000 });
      } else {
        toast.success(baseMsg);
      }
      await onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); } finally { setSaving(false); }
  };

  const computeAmount = () => {
    if (!selected) return 0;
    if (isDeposit) return selected.total_ttc * depositPct / 100;
    if (isSituation) return selected.total_ttc * deltaPct / 100;
    return selected.total_ttc;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="card max-w-xl w-full" onClick={(e) => e.stopPropagation()} data-testid="invoice-modal">
        <div className="px-6 py-4 border-b flex justify-between items-center" style={{ borderColor: "#3A3D42" }}>
          <h3 className="font-display text-xl tracking-tight" style={{ fontWeight: 600 }}>Nouvelle facture</h3>
          <button onClick={onClose} className="btn btn-ghost btn-sm"><X size={14} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="label">Devis source *</label>
            <select className="input" value={quoteId} onChange={(e) => setQuoteId(e.target.value)} data-testid="inv-quote">
              <option value="">— Sélectionnez un devis —</option>
              {acceptedQuotes.map((q) => {
                const c = clients.find((x) => x.id === q.client_id);
                return <option key={q.id} value={q.id}>{q.number} · {c?.last_name} · {fmtEUR(q.total_ttc)}</option>;
              })}
            </select>
            {acceptedQuotes.length === 0 && <div className="text-xs text-[#FFCF40] mt-1">Aucun devis disponible — créez d'abord un devis.</div>}
          </div>

          {/* Type de facture */}
          <div>
            <label className="label">Type de facture</label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { k: "complete", label: "Facture complète", desc: "100% du devis" },
                { k: "deposit", label: "Acompte", desc: "Avant chantier" },
                { k: "situation", label: "Situation", desc: "Gros chantier" },
              ].map(({ k, label, desc }) => {
                const active = invoiceType === k;
                return (
                  <button
                    key={k}
                    onClick={() => setInvoiceType(k)}
                    className="text-left p-3 rounded-lg border transition-all"
                    style={{
                      background: active ? "#FFC400" : "#161618",
                      color: active ? "#161618" : "#F5F4F2",
                      borderColor: active ? "#FFC400" : "#3A3D42",
                    }}
                    data-testid={`inv-type-${k}`}
                  >
                    <div className="font-display text-sm" style={{ fontWeight: 600 }}>{label}</div>
                    <div className="text-[11px] opacity-70 mt-0.5">{desc}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {isDeposit && (
            <div className="rounded-lg p-4 border" style={{ background: "rgba(255,196,0,0.10)", borderColor: "#E5D2B5" }}>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium" style={{ color: "#7A4F1F" }}>Pourcentage d'acompte :</span>
                <input type="number" min="1" max="99" className="input w-24 numerals text-right" value={depositPct} onChange={(e) => setDepositPct(Number(e.target.value))} data-testid="inv-deposit-pct" />
                <span style={{ color: "#7A4F1F" }}>%</span>
              </div>
            </div>
          )}

          {isSituation && (
            <div className="rounded-lg p-4 border space-y-3" style={{ background: "rgba(255,196,0,0.30)", borderColor: "#BDD2C5" }}>
              {situationsData && situationsData.situations.length > 0 && (
                <div className="text-xs font-body" style={{ color: "#0A3A30" }}>
                  <b>{situationsData.situations.length}</b> situation{situationsData.situations.length > 1 ? "s" : ""} déjà émise{situationsData.situations.length > 1 ? "s" : ""} sur ce devis · Cumul actuel : <b className="numerals">{situationsData.cumulative_pct}%</b>
                </div>
              )}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="label">N° situation</label>
                  <input type="number" min="1" className="input numerals" value={situationNumber} onChange={(e) => setSituationNumber(Number(e.target.value))} data-testid="inv-situation-num" />
                </div>
                <div>
                  <label className="label">Cumul précédent %</label>
                  <input type="number" min="0" max="100" className="input numerals" value={previousPct} onChange={(e) => setPreviousPct(Number(e.target.value))} data-testid="inv-prev-pct" />
                </div>
                <div>
                  <label className="label">Avancement cumulé %</label>
                  <input type="number" min="0" max="100" className="input numerals" value={cumulativePct} onChange={(e) => setCumulativePct(Number(e.target.value))} data-testid="inv-cum-pct" />
                </div>
              </div>
              <div className="text-sm font-body" style={{ color: "#0A3A30" }}>
                Cette situation facturera <b className="numerals">+{deltaPct}%</b> du devis (delta entre <b>{previousPct}%</b> et <b>{cumulativePct}%</b>).
              </div>
            </div>
          )}

          {selected && (
            <div className="p-4 rounded-lg text-sm space-y-1" style={{ background: "#F5F4F2", color: "#161618" }}>
              <div className="flex justify-between"><span>Devis source</span><span className="numerals">{selected.number}</span></div>
              <div className="flex justify-between"><span>Total devis TTC</span><span className="numerals">{fmtEUR(selected.total_ttc)}</span></div>
              <div className="flex justify-between font-display text-lg pt-2 mt-1 border-t border-white/20" style={{ fontWeight: 600 }}>
                <span>Cette facture (TTC)</span>
                <span className="numerals" style={{ color: "#FFC400" }}>{fmtEUR(computeAmount())}</span>
              </div>
            </div>
          )}
        </div>
        <div className="px-6 py-4 border-t flex justify-end gap-2" style={{ borderColor: "#3A3D42" }}>
          <button onClick={onClose} className="btn btn-outline">Annuler</button>
          <button onClick={save} disabled={saving || !quoteId} className="btn btn-primary" data-testid="inv-save">
            <Save size={14} /> {saving ? "..." : "Créer la facture"}
          </button>
        </div>
      </div>
    </div>
  );
}

function PaymentModal({ invoice, onClose, onSaved }) {
  const [amount, setAmount] = useState(invoice.balance);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [method, setMethod] = useState("virement");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!Number(amount)) { toast.error("Montant requis"); return; }
    setSaving(true);
    try {
      await api.post(`/invoices/${invoice.id}/payments`, { amount: Number(amount), date, method });
      toast.success("Paiement enregistré");
      await onSaved();
    } catch (e) { toast.error("Erreur"); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="card max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b flex justify-between items-center"><h3 className="font-semibold">Paiement — {invoice.number}</h3><button onClick={onClose} className="btn btn-ghost btn-sm"><X size={14} /></button></div>
        <div className="p-6 space-y-4">
          <div className="text-sm text-secondary-text">Solde restant : <b className="font-mono">{fmtEUR(invoice.balance)}</b></div>
          <div><label className="label">Montant encaissé</label><input type="number" step="0.01" className="input text-right font-mono" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="pay-amount" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="label">Date</label><input type="date" className="input" value={date} onChange={(e) => setDate(e.target.value)} /></div>
            <div><label className="label">Mode</label><select className="input" value={method} onChange={(e) => setMethod(e.target.value)}><option value="virement">Virement</option><option value="cheque">Chèque</option><option value="cb">CB</option><option value="especes">Espèces</option></select></div>
          </div>
        </div>
        <div className="px-6 py-4 border-t flex justify-end gap-2"><button onClick={onClose} className="btn btn-outline">Annuler</button><button onClick={save} disabled={saving} className="btn btn-primary" data-testid="pay-save"><Save size={14} /> Enregistrer</button></div>
      </div>
    </div>
  );
}
